<script setup>
// The two live bands of the overview (R1): open conversations (with the team picker the woot has when the account has
// teams) and agent status, each a SectionBand with the "Live" mark and the kit's tiles. Same getters and the same
// live refresh as before.
import { computed, onMounted, ref } from 'vue';
import { OVERVIEW_METRICS } from '../constants';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { useI18n } from 'vue-i18n';
import SectionBand from 'dashboard/components-next/polaris/reports/SectionBand.vue';
import StatTile from 'dashboard/components-next/polaris/cards/StatTile.vue';
import OptionSelect from 'dashboard/components-next/polaris/cards/OptionSelect.vue';
import LiveBadge from './overview/LiveBadge.vue';

const { t } = useI18n();

const uiFlags = useMapGetter('getOverviewUIFlags');
const agentStatus = useMapGetter('agents/getAgentStatus');
const accountConversationMetric = useMapGetter('getAccountConversationMetric');
const store = useStore();

const accounti18nKey = 'OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS';
const teams = useMapGetter('teams/getTeams');

const teamOptions = computed(() => [
  { label: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.ALL_TEAMS'), value: '' },
  ...teams.value.map(team => ({ label: team.name, value: team.id })),
]);

const toTiles = (keys, source, prefix) =>
  keys.map(key => ({
    key,
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    label: t(`${prefix}.${OVERVIEW_METRICS[key]}`),
    value: Number(source[key] || 0).toLocaleString(),
  }));
const agentStatusTiles = computed(() =>
  toTiles(
    ['online', 'busy', 'offline'],
    agentStatus.value,
    'OVERVIEW_REPORTS.AGENT_STATUS'
  )
);
const conversationTiles = computed(() =>
  toTiles(
    ['open', 'unattended', 'unassigned', 'pending'],
    accountConversationMetric.value,
    accounti18nKey
  )
);
// A skeleton only until the first answer: the live refresh does not blink the numbers every few seconds.
const isFirstLoad = computed(
  () =>
    uiFlags.value.isFetchingAccountConversationMetric &&
    !Object.keys(accountConversationMetric.value).length
);

const selectedTeam = ref('');
const fetchData = () => {
  const params = {};
  if (selectedTeam.value) {
    params.team_id = selectedTeam.value;
  }
  store.dispatch('fetchAccountConversationMetric', params);
};

const { startRefetching } = useLiveRefresh(fetchData);

const onTeamChange = value => {
  selectedTeam.value = value;
  fetchData();
};

onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <section class="grid min-w-0 gap-4" data-live-conversations>
    <SectionBand :title="t(`${accounti18nKey}.HEADER`)">
      <template #badge>
        <LiveBadge />
      </template>
      <template v-if="teams.length" #actions>
        <div class="w-48 min-w-0">
          <OptionSelect
            :model-value="selectedTeam"
            :options="teamOptions"
            size="sm"
            :aria-label="t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.HEADER')"
            @update:model-value="onTeamChange"
          />
        </div>
      </template>
    </SectionBand>
    <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatTile
        v-for="tile in conversationTiles"
        :key="tile.key"
        :label="tile.label"
        :value="tile.value"
        :loading="isFirstLoad"
      />
    </div>
  </section>
  <section class="grid min-w-0 gap-4" data-live-agent-status>
    <SectionBand :title="t('OVERVIEW_REPORTS.AGENT_STATUS.HEADER')">
      <template #badge>
        <LiveBadge />
      </template>
    </SectionBand>
    <div class="grid gap-4 sm:grid-cols-3">
      <StatTile
        v-for="tile in agentStatusTiles"
        :key="tile.key"
        :label="tile.label"
        :value="tile.value"
      />
    </div>
  </section>
</template>
